import { QuestionRulesConditions } from '@app/src/components/Drawer/Views/DrawerViewAddQuestionRule'
import { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useReport } from '../pages/Questionnaire/ReportContext'
import { AnswerStatus, Request, RequestItem } from '../types/resourceExplorer'

const getRequestItemIdFromQuestionId = (questionId: number, request?: Request) => {
  return request?.sections?.flatMap(section => section.items).find(item => item.questionId === questionId)?.id
}

const useQuestionRules = (requestItem: RequestItem) => {
  const { requestItemsRules, request } = useReport()
  const { setValue, getValues, trigger } = useFormContext()

  const rulesAffectingCurrentQuestion = useMemo(
    () =>
      requestItemsRules?.filter(rule =>
        rule.sectionQuestionTargets.some(target => target.targetQuestionId === requestItem.questionId),
      ),
    [requestItemsRules, requestItem.questionId],
  )

  const rulesTriggeredByCurrentQuestion = useMemo(
    () => requestItemsRules?.filter(rule => rule.questionId === requestItem.questionId),
    [requestItemsRules, requestItem.questionId],
  )

  const applySkipRuleEffectsToQuestion = useCallback(
    (triggerQuestionId: number, selectedValues: string[] | string | undefined) => {
      if (!rulesAffectingCurrentQuestion?.length) return

      const valuesArray = selectedValues ? (Array.isArray(selectedValues) ? selectedValues : [selectedValues]) : []
      const triggerRequestItemId = getRequestItemIdFromQuestionId(triggerQuestionId, request)
      const triggerCannotAnswer = getValues(`${triggerRequestItemId}.cannotAnswer`)
      const triggerAnswerStatus = getValues(`${triggerRequestItemId}.answerStatus`)
      const hasFileUploaded = Boolean(getValues(`${triggerRequestItemId}.uploadReferences.originalFileName`))

      const isRuleMatched = rulesAffectingCurrentQuestion.some(rule => {
        if (triggerCannotAnswer && rule.questionId === triggerQuestionId) {
          const isSkipRule = rule.conditionValue === QuestionRulesConditions.Skipped
          if (isSkipRule) return true
        }

        if (
          rule.questionId === triggerQuestionId &&
          rule.conditionValue === QuestionRulesConditions.FileUploaded &&
          hasFileUploaded
        ) {
          return true
        }

        return rule.questionId === triggerQuestionId && valuesArray.includes(rule.conditionValue)
      })

      const requestItemId = requestItem.id
      const answer = `${requestItemId}.answer`
      const selectedCollection = `${requestItemId}.selectedOptions`
      const fileUpload = `${requestItemId}.uploadReferences`
      const cannotAnswer = `${requestItemId}.cannotAnswer`
      const answerStatus = `${requestItemId}.answerStatus`
      const currentStatus = getValues(answerStatus)

      const isCurrentQuestionAnswered =
        Boolean(getValues(answer)?.trim()?.length) ||
        Boolean(getValues(selectedCollection)?.length) ||
        Boolean(getValues(fileUpload)?.originalFileName?.length) ||
        (getValues(cannotAnswer) && getValues(answerStatus) !== AnswerStatus.Excluded)

      const needExclude =
        isRuleMatched &&
        currentStatus !== AnswerStatus.Excluded &&
        triggerAnswerStatus !== AnswerStatus.Excluded &&
        !isCurrentQuestionAnswered

      const needInclude =
        !isRuleMatched &&
        currentStatus === AnswerStatus.Excluded &&
        rulesAffectingCurrentQuestion.some(rule => rule.questionId === triggerQuestionId)

      const isExcludedByOtherRules = rulesAffectingCurrentQuestion.some(rule => {
        if (rule.questionId === triggerQuestionId) return false

        const otherTriggerItemId = getRequestItemIdFromQuestionId(rule.questionId, request)
        const otherSelectedValues = getValues(`${otherTriggerItemId}.selectedOptions`)
        const otherCannotAnswer = getValues(`${otherTriggerItemId}.cannotAnswer`)
        const otherHasFileUploaded = Boolean(getValues(`${otherTriggerItemId}.uploadReferences.originalFileName`))

        if (otherCannotAnswer && rule.conditionValue === QuestionRulesConditions.Skipped) {
          return true
        }

        if (rule.conditionValue === QuestionRulesConditions.FileUploaded && otherHasFileUploaded) {
          return true
        }

        const otherValuesArray = otherSelectedValues
          ? Array.isArray(otherSelectedValues)
            ? otherSelectedValues
            : [otherSelectedValues]
          : []

        return otherValuesArray.includes(rule.conditionValue)
      })

      const applyFormUpdates = (updates: Array<{ name: string; value: unknown }>) => {
        updates.forEach(({ name, value }) => {
          setValue(name, value, { shouldDirty: true })
        })

        setTimeout(() => {
          trigger(answerStatus)
          trigger(`${requestItemId}`)
        }, 0)
      }

      if (needExclude) {
        applyFormUpdates([
          { name: selectedCollection, value: undefined },
          { name: answer, value: undefined },
          { name: fileUpload, value: undefined },
          { name: cannotAnswer, value: true },
          { name: answerStatus, value: AnswerStatus.Excluded },
        ])
      } else if (needInclude && !isExcludedByOtherRules) {
        applyFormUpdates([
          { name: cannotAnswer, value: false },
          { name: answerStatus, value: undefined },
        ])
      }
    },
    [requestItem.id, rulesAffectingCurrentQuestion, setValue, getValues, trigger],
  )

  return {
    rulesAffectingCurrentQuestion,
    rulesTriggeredByCurrentQuestion,
    applySkipRuleEffectsToQuestion,
  }
}

export default useQuestionRules
